import React, { createContext, useContext, useState, useEffect } from 'react';
import { sfAuthAPI, sfCustomerAPI, setCustomerToken, getCustomerToken, removeCustomerToken } from '../services/storefront';
import { getSessionToken } from '../utils/session';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Rehydrate from localStorage on mount
  useEffect(() => {
    const token = getCustomerToken();
    const saved = localStorage.getItem('storefront-customer-user');
    if (token && saved) {
      try {
        setUser(JSON.parse(saved));
        setIsAuthenticated(true);
      } catch {
        removeCustomerToken();
        localStorage.removeItem('storefront-customer-user');
      }
    }
    setLoading(false);
  }, []);

  const _persist = (token, customer) => {
    setCustomerToken(token);
    localStorage.setItem('storefront-customer-user', JSON.stringify(customer));
    setUser(customer);
    setIsAuthenticated(true);
  };

  const login = async (email, password) => {
    try {
      const sessionTokenBeforeLogin = getSessionToken();
      const data = await sfAuthAPI.login({ email, password });
      _persist(data.token, data.customer);

      // Merge guest cart into customer cart (non-critical)
      try {
        const { sfCartAPI } = await import('../services/storefront');
        await sfCartAPI.merge(sessionTokenBeforeLogin);
      } catch (mergeErr) {
        console.warn('Cart merge failed:', mergeErr);
      }

      return { success: true, user: data.customer };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed. Please check your credentials.',
      };
    }
  };

  const register = async ({ name, email, password, phone }) => {
    try {
      const data = await sfAuthAPI.register({ name, email, password, phone });
      _persist(data.token, data.customer);
      return { success: true, user: data.customer };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed. Please try again.',
      };
    }
  };

  const logout = () => {
    removeCustomerToken();
    localStorage.removeItem('storefront-customer-user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (profileData) => {
    try {
      const updated = await sfCustomerAPI.updateProfile(profileData);
      const merged = { ...user, ...updated };
      localStorage.setItem('storefront-customer-user', JSON.stringify(merged));
      setUser(merged);
      return { success: true, user: merged };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Profile update failed.',
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      await sfAuthAPI.forgotPassword(email);
    } catch {
      // Swallow — always return success to prevent user enumeration
    }
    return { success: true };
  };

  const resetPassword = async ({ token, newPassword }) => {
    try {
      await sfAuthAPI.resetPassword({ token, newPassword });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Password reset failed.',
      };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      login,
      register,
      logout,
      updateProfile,
      forgotPassword,
      resetPassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
